import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useFormik } from "formik";
import { useDispatch } from "react-redux";
import { Dispatch, RootState } from "shared/store/store";
import { useSelector } from "react-redux";
import { fieldsFormDeduction } from "../helpers/fieldsFormDeduction";
import { columnsDeductions } from "../helpers/columnsDeductions";
import { IDeduction } from "../models/IDeduction";
import { ModalDeduction } from "../components/ModalDeduction";
import Swal from "sweetalert2";
import {
  postDeduction,
  PostDeductionRequest,
} from "shared/api/services/postDeduction";
import {
  putDeduction,
  PutDeductionRequest,
} from "shared/api/services/putDeduction";
import { deleteDeduction as deleteDeductionApi } from "shared/api/services/deleteDeduction";
import { getDeductions } from "shared/api/services/getDeductions";

interface UseDeductionsResult {
  handleChange: (e: React.ChangeEvent<any>) => void;
  handleSubmit: (e?: React.FormEvent<HTMLFormElement> | undefined) => void;
  columns: any[];
  valuesResultsPages: number[];
  showForm: boolean;
  dataFields: any[];
  getValue: (name: string) => string;
  toogleShowForm: () => void;
  createDeduction: () => void;
  clearFilters: () => void;
}

export const useDeductions = (): UseDeductionsResult => {
  const dispatch = useDispatch<Dispatch>();
  const { content, paramsUrl, url, totalElements } = useSelector(
    (state: RootState) => state.electronicDocuments,
  );

  const toDDMMYYYYFromISO = (value: string): string => {
    const trimmed = (value ?? "").trim();
    const match = trimmed.match(/^(\d{4})-(\d{2})-(\d{2})$/);
    if (!match) return "";
    const [, yyyy, mm, dd] = match;
    return `${dd}/${mm}/${yyyy}`;
  };

  const [showForm, setShowForm] = useState(false);
  const valuesResultsPages = useMemo(() => [50, 100, 250, 500], []);
  const dataFields = useMemo(() => fieldsFormDeduction(), []);

  const toogleShowForm = () => setShowForm((prev) => !prev);

  const { handleChange, handleSubmit, values, resetForm } = useFormik({
    initialValues: fieldsFormDeduction().reduce<Record<string, string>>(
      (acc, field) => {
        acc[field.name] = "";
        return acc;
      },
      {},
    ),
    onSubmit: async () => {
      const params: any = { page: 0, size: 50 };
      if (values.conceptCode) params.conceptCode = values.conceptCode;
      if (values.operatingIndicator)
        params.operatingIndicator = values.operatingIndicator;

      try {
        const data = await getDeductions(params);

        const content = data.content.map((item) => ({
          id: item.id,
          conceptCode: item.conceptCode,
          description: item.conceptDescription,
          accountingAccount: item.accountingAccount,
          percentage: String(item.percentage),
          fiscalYear: String(item.fiscalYear),
          startDate: item.effectiveStartDate,
          endDate: item.effectiveEndDate,
          operatingIndicator: item.operatingIndicator,
          beneficiary: item.beneficiary,
          beneficiaryName: item.beneficiaryName,
          value: 0,
          status: "ACTIVA",
        }));

        dispatch.electronicDocuments.cleanState();
        dispatch.electronicDocuments.addState({
          paramsUrl: { page: 0, size: 50, ...params },
          url: "/v1/deductions",
          res: {
            content,
            totalElements: data.totalElements,
            number: data.number,
            size: data.size,
            contentSize: data.contentSize,
          },
        });
      } catch (error) {
        console.error("Error filtering deductions:", error);
      }
    },
    validateOnChange: false,
    enableReinitialize: true,
  });

  const getValue = (name: string) => {
    const valueField = Object.entries(values).find(([key]) => key === name);
    return valueField ? (valueField[1] as string) : "";
  };

  const clearFilters = async () => {
    resetForm();
    try {
      const data = await getDeductions({ page: 0, size: 50 });

      const content = data.content.map((item) => ({
        id: item.id,
        conceptCode: item.conceptCode,
        description: item.conceptDescription,
        accountingAccount: item.accountingAccount,
        percentage: String(item.percentage),
        fiscalYear: String(item.fiscalYear),
        startDate: item.effectiveStartDate,
        endDate: item.effectiveEndDate,
        operatingIndicator: item.operatingIndicator,
        value: 0,
        status: "ACTIVA",
      }));

      dispatch.electronicDocuments.cleanState();
      dispatch.electronicDocuments.addState({
        paramsUrl: { page: 0, size: 50 },
        url: "/v1/deductions",
        res: {
          content,
          totalElements: data.totalElements,
          number: data.number,
          size: data.size,
          contentSize: data.contentSize,
        },
      });
    } catch (error) {
      console.error("Error clearing filters:", error);
    }
  };

  const createDeduction = () => {
    const onClose = () => {
      dispatch.messageModal.cleanStateMessage();
    };

    const handleCreateSubmit = async (values: Record<string, string>) => {
      const payload: PostDeductionRequest = {
        conceptCode: values.conceptCode,
        conceptDescription: values.description,
        accountingAccount: values.accountingAccount,
        percentage: Number(values.percentage),
        fiscalYear: Number(values.fiscalYear),
        effectiveStartDate: values.startDate,
        effectiveEndDate: values.endDate,
        operatingIndicator: values.operatingIndicator,
        beneficiary: values.beneficiary,
        beneficiaryName: values.beneficiaryName,
      };

      const response = await postDeduction(payload);

      const nextRow = {
        id: response.id,
        conceptCode: values.conceptCode,
        description: values.conceptDescription,
        accountingAccount: values.accountingAccount,
        percentage: values.percentage,
        fiscalYear: values.fiscalYear,
        startDate: values.effectiveStartDate,
        endDate: values.effectiveEndDate,
        operatingIndicator: values.operatingIndicator,
        value: 0,
        status: "ACTIVA",
      };

      const current = (content as any[]) ?? [];
      const next = [nextRow, ...current];
      const currentTotalElements = totalElements || 0;

      dispatch.electronicDocuments.addState({
        paramsUrl: { page: 0, size: 50 },
        url: "/v1/deductions",
        res: {
          content: next,
          totalElements: currentTotalElements + 1,
          number: 0,
          size: 50,
          contentSize: next.length,
        },
      });

      Swal.fire({
        icon: "success",
        title: "Deducción creada exitosamente",
        confirmButtonText: "Aceptar",
      });

      onClose();
    };

    dispatch.messageModal.setMessageModal({
      show: true,
      title: "Crear deducción",
      onClose() {
        onClose();
      },
      size: "large",
      description: React.createElement(ModalDeduction, {
        onClose,
        onSubmit: handleCreateSubmit,
      }),
    });
  };

  const editDeduction = useCallback(
    (row: IDeduction & Record<string, any>) => {
      const onClose = () => {
        dispatch.messageModal.cleanStateMessage();
      };

      const handleSubmit = async (values: Record<string, string>) => {
        if (!row.id) return;

        const payload: PutDeductionRequest = {
          conceptCode: values.conceptCode,
          conceptDescription: values.description,
          accountingAccount: values.accountingAccount,
          percentage: Number(values.percentage),
          fiscalYear: Number(values.fiscalYear),
          effectiveStartDate: values.startDate,
          effectiveEndDate: values.endDate,
          operatingIndicator: values.operatingIndicator,
          beneficiary: values.beneficiary,
          beneficiaryName: values.beneficiaryName,
        };

        const response = await putDeduction(row.id, payload);

        const updatedRow = {
          id: response.id,
          ...values,
          percentage: values.percentage,
          fiscalYear: values.fiscalYear,
          startDate: values.startDate,
          endDate: values.endDate,
          operatingIndicator: values.operatingIndicator,
        };

        const current = (content as any[]) ?? [];
        const next = current.map((x) => (x?.id === row?.id ? updatedRow : x));

        dispatch.electronicDocuments.addState({
          paramsUrl: {},
          url: "",
          res: {
            content: next,
            totalElements: next.length,
            number: 0,
            size: next.length,
            contentSize: next.length,
          },
        });

        Swal.fire({
          icon: "success",
          title: "Deducción actualizada exitosamente",
          confirmButtonText: "Aceptar",
        });

        onClose();
      };

      dispatch.messageModal.setMessageModal({
        show: true,
        title: "Editar deducción",
        onClose() {
          onClose();
        },
        size: "large",
        description: React.createElement(ModalDeduction, {
          onClose,
          deduction: row,
          onSubmit: handleSubmit,
        }),
      });
    },
    [content, dispatch.electronicDocuments, dispatch.messageModal],
  );

  const deleteDeduction = useCallback(
    (row: IDeduction & Record<string, any>) => {
      if (!content) return;

      // Confirmación antes de eliminar
      Swal.fire({
        title: "¿Está seguro?",
        text: "¿Desea eliminar esta deducción?",
        icon: "warning",
        showCancelButton: true,
        confirmButtonColor: "#3085d6",
        cancelButtonColor: "#d33",
        confirmButtonText: "Sí, eliminar",
        cancelButtonText: "Cancelar",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            // Llamar al servicio para eliminar del backend
            await deleteDeductionApi(row.id!);

            // Actualizar estado local
            const next = (content as any[]).filter((x) => x?.id !== row?.id);
            dispatch.electronicDocuments.addState({
              paramsUrl: { page: 0, size: 50 },
              url: "/v1/deductions",
              res: {
                content: next,
                totalElements: next.length,
                number: 0,
                size: 50,
                contentSize: next.length,
              },
            });

            Swal.fire(
              "Eliminado",
              "La deducción ha sido eliminada correctamente",
              "success",
            );
          } catch (error) {
            console.error("Error deleting deduction:", error);
            Swal.fire("Error", "No se pudo eliminar la deducción", "error");
          }
        }
      });
    },
    [content, dispatch.electronicDocuments],
  );

  useEffect(() => {
    return () => {
      resetForm();
    };
  }, [resetForm]);

  const columns = useMemo(
    () => columnsDeductions(editDeduction, deleteDeduction),
    [deleteDeduction, editDeduction],
  );

  useEffect(() => {
    const fetchDeductions = async () => {
      try {
        const data = await getDeductions({ page: 0, size: 50 });

        const content = data.content.map((item) => ({
          id: item.id,
          conceptCode: item.conceptCode,
          description: item.conceptDescription,
          accountingAccount: item.accountingAccount,
          percentage: String(item.percentage),
          fiscalYear: String(item.fiscalYear),
          startDate: item.effectiveStartDate,
          endDate: item.effectiveEndDate,
          operatingIndicator: item.operatingIndicator,
          beneficiary: item.beneficiary,
          beneficiaryName: item.beneficiaryName,
          value: 0,
          status: "ACTIVA",
        }));

        dispatch.electronicDocuments.cleanState();
        dispatch.electronicDocuments.addState({
          paramsUrl: { page: 0, size: 50 },
          url: "/v1/deductions",
          res: {
            content,
            totalElements: data.totalElements,
            number: data.number,
            size: data.size,
            contentSize: data.contentSize,
          },
        });
      } catch (error) {
        console.error("Error fetching deductions:", error);
      }
    };

    fetchDeductions();
  }, []);

  useEffect(() => {
    const fetchDeductionsPaginated = async () => {
      if (!url || !url.includes("deductions")) return;

      try {
        const params: any = {
          page: paramsUrl?.page || 0,
          size: paramsUrl?.size || 50,
        };
        if ((paramsUrl as any)?.orderBy)
          params.orderBy = (paramsUrl as any).orderBy;
        if ((paramsUrl as any)?.conceptCode)
          params.conceptCode = (paramsUrl as any).conceptCode;
        if ((paramsUrl as any)?.operatingIndicator)
          params.operatingIndicator = (paramsUrl as any).operatingIndicator;

        const data = await getDeductions(params);

        const content = data.content.map((item) => ({
          id: item.id,
          conceptCode: item.conceptCode,
          description: item.conceptDescription,
          accountingAccount: item.accountingAccount,
          percentage: String(item.percentage),
          fiscalYear: String(item.fiscalYear),
          startDate: item.effectiveStartDate,
          endDate: item.effectiveEndDate,
          operatingIndicator: item.operatingIndicator,
          beneficiary: item.beneficiary,
          beneficiaryName: item.beneficiaryName,
          value: 0,
          status: "ACTIVA",
        }));

        dispatch.electronicDocuments.addState({
          paramsUrl,
          url,
          res: {
            content,
            totalElements: data.totalElements,
            number: data.number,
            size: data.size,
            contentSize: data.contentSize,
          },
        });
      } catch (error) {
        console.error("Error fetching paginated deductions:", error);
      }
    };

    fetchDeductionsPaginated();
  }, [paramsUrl, url, dispatch.electronicDocuments]);

  return {
    handleChange,
    handleSubmit,
    columns,
    valuesResultsPages,
    showForm,
    dataFields,
    getValue,
    toogleShowForm,
    createDeduction,
    clearFilters,
  };
};
